import React from 'react';
import bgTable from '../../../assets/images/dyslexia/bg_table.jpg';

const dyslexiaData = {
  leftSide: [
    {
      title: 'Preschool (Ages 3-5)',
      content: [
        'Delayed speech development.',
        'Difficulty pronouncing long words, sometimes mixing up syllables or sounds.',
        'Trouble recognizing and producing rhymes.',
        'Difficulty remembering the alphabet, colors, and numbers.',
        'A family history of reading difficulties can also be a significant indicator.',
      ],
    },
    {
      title: 'Elementary School (Ages 6-12)',
      content: [
        'Reading well below the expected level for their age.',
        'Difficulty with spelling, including inconsistent and unpredictable errors.',
        'Trouble sounding out new or unfamiliar words.',
        "Confusing letters that look similar, like 'b' and 'd', or sound similar, like 'f' and 'v'.",
        'Difficulties with math, such as memorizing multiplication tables, can also be a sign.',
      ],
    },
    {
      title: 'Middle & High School (Ages 13-18)',
      content: [
        'Slow, effortful reading and writing that requires significant concentration.',
        'Poorly organized written work despite having strong verbal skills.',
        'Significant difficulty learning a foreign language.',
        'A tendency to avoid reading and writing activities.',
      ],
    },
  ],
  rightSide: [
    {
      title: 'Understanding Co-Occurring Conditions',
      content:
        'It’s common for dyslexia to co-exist with other conditions. A comprehensive evaluation should consider these possibilities:',
    },
    {
      title: 'Attention-Deficit/Hyperactivity Disorder (ADHD)',
      content:
        'Research shows a significant overlap between dyslexia and ADHD, as both can affect executive functions like memory and organization.',
    },
    {
      title: 'Dysgraphia',
      content:
        'This involves difficulties with the physical act of writing (handwriting) and expressing thoughts in writing.',
    },
    {
      title: 'Dyscalculia',
      content:
        'This is a specific learning disability in math that can also be significant.',
    },
    {
      title: 'Anxiety and Depression',
      content:
        'The ongoing challenges and frustrations of dyslexia can sometimes lead to emotional difficulties.',
    },
  ],
};

const DyslexiaSigns = () => {
  return (
    <section className="relative w-full overflow-hidden bg-[#163D69]">
      <div
        className="absolute inset-0 bg-cover bg-center bg-no-repeat"
        style={{
          backgroundImage: `url(${bgTable})`,
          filter: 'blur(5px)',
        }}
      ></div>
      <div
        className="absolute inset-0 bg-opacity-70"
        style={{
          backgroundColor: 'rgba(22, 61, 105, 0.7)',
        }}
      ></div>
      <div className="gap-4 sm:gap-6 max-w-[1150px] mx-auto px-4 md:px-10 py-10 md:py-20 relative z-10">
        <div className="mb-8">
          <h1 className="text-[38px] sm:text-[40px] md:text-[44px] text-white font-bold">
            Recognizing the Signs of Dyslexia Across Different Ages
          </h1>
          <h2 className="text-[20px] sm:text-[24px] md:text-[30px] text-white font-bold leading-tight">
            The signs of dyslexia can appear at different stages of development.
            <br />
            Here’s what to look for:
          </h2>
        </div>
        <div className="w-full flex flex-col lg:flex-row justify-between gap-8">
          <div className="w-full lg:w-1/2 space-y-6">
            {dyslexiaData.leftSide.map((card, index) => (
              <div key={index} className="">
                <div className="bg-gradient-to-r from-[#C572A7] to-[#2E3192] px-4 py-2 rounded-t-xl">
                  <h2 className="text-white text-2xl font-semibold">
                    {card.title}
                  </h2>
                </div>
                <div className="bg-[#D4EDF8] rounded-b-xl shadow-lg px-4 py-2">
                  <ul className="text-black mt-4 mb-4 space-y-2 list-disc pl-6">
                    {card.content.map((item, idx) => (
                      <li key={idx} className="text-lg -mt-4 leading-[18px]">
                        {item}
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            ))}
          </div>

          {/* Right Side */}
          <div className="w-full lg:w-1/2 space-y-6">
            {dyslexiaData.rightSide.map((box, index) => (
              <div
                key={index}
                className={`p-4 rounded-xl shadow-lg ${
                  index === 0
                    ? 'bg-white text-[#183E6D]'
                    : 'bg-gradient-to-r to-[#00A9F4] from-[#25516f] text-white'
                }`}
              >
                <h3 className={`text-2xl font-semibold`}>{box.title}</h3>
                {index === 0 ? (
                  <p className="text-lg font-semibold">{box.content}</p>
                ) : (
                  <ul className="list-disc pl-6">
                    <li>{box.content}</li>
                  </ul>
                )}
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};

export default DyslexiaSigns;
